import { Controller, useFieldArray } from 'react-hook-form';
import type { Control, UseFormWatch } from 'react-hook-form';
import { Input } from '../../tailwindComponents/Input';
import { OFF_ROUTE_DISTANCE_UNIT } from '../../constants/ratecards';

interface PalletRate {
  pallets: number;
  price?: number;
}

interface WeightRate {
  minWeight: number;
  maxWeight?: number | null;
  price?: number;
}

export interface LoadSizeMatrixFormValues {
  rateMode: 'pallet' | 'weight';
  palletRates: PalletRate[];
  weightRates: WeightRate[];
  maxOffRouteDistance?: number;
  chargePerAdditionalDrop?: number;
  standardIncludedLoadingMinutes?: number;
  standardIncludedUnloadingMinutes?: number;
  chargePerAdditionalHour?: number;
  chargePerOvertimeHour?: number;
}

export const DEFAULT_PALLET_RATES: PalletRate[] = Array.from(
  { length: 10 },
  (_, i) => ({ pallets: i + 1, price: undefined })
);

export const DEFAULT_WEIGHT_RATES: WeightRate[] = [
  { minWeight: 0, maxWeight: 500, price: undefined },
  { minWeight: 500, maxWeight: 1000, price: undefined },
  { minWeight: 1000, maxWeight: 1500, price: undefined },
  { minWeight: 1500, maxWeight: 2000, price: undefined },
  { minWeight: 2000, maxWeight: 3000, price: undefined },
  { minWeight: 3000, maxWeight: 5000, price: undefined },
  { minWeight: 5000, maxWeight: 7500, price: undefined },
  { minWeight: 7500, maxWeight: 10000, price: undefined },
  { minWeight: 10000, maxWeight: 15000, price: undefined },
  { minWeight: 15000, maxWeight: null, price: undefined },
];

interface LoadSizeMatrixFieldsProps {
  control: Control<any>;
  watch: UseFormWatch<any>;
  rateError?: string | null;
}

export function LoadSizeMatrixFields({
  control,
  watch,
  rateError,
}: LoadSizeMatrixFieldsProps) {
  const rateMode = watch('rateMode');

  const { fields: palletFields } = useFieldArray({
    control,
    name: 'palletRates',
  });

  const { fields: weightFields } = useFieldArray({
    control,
    name: 'weightRates',
  });

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <h3 className='text-lg font-semibold text-gray-900'>Load Size Matrix</h3>
        <p className='text-sm text-gray-600'>
          Enter prices for the load size bands, and the charges for additional
          drops and labour time.
        </p>
      </div>

      {/* Rate Mode Toggle */}
      <div className='flex flex-col gap-2'>
        <label className='text-sm font-medium text-gray-700'>Rate Type</label>
        <Controller
          name='rateMode'
          control={control}
          render={({ field }) => (
            <div className='flex gap-4'>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='radio'
                  value='pallet'
                  checked={field.value === 'pallet'}
                  onChange={() => field.onChange('pallet')}
                  className='w-4 h-4 text-blue-600'
                />
                <span className='text-sm text-gray-700'>Pallet Rates</span>
              </label>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='radio'
                  value='weight'
                  checked={field.value === 'weight'}
                  onChange={() => field.onChange('weight')}
                  className='w-4 h-4 text-blue-600'
                />
                <span className='text-sm text-gray-700'>Weight Rates</span>
              </label>
            </div>
          )}
        />
      </div>

      {/* Pallet Rates Table */}
      {rateMode === 'pallet' && (
        <div className='border border-gray-200 rounded-lg overflow-hidden'>
          <table className='w-full'>
            <thead>
              <tr className='bg-gray-50'>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Consignment Size
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Base Charge
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {palletFields.map((field, index) => (
                <tr key={field.id} className='hover:bg-gray-50'>
                  <td className='px-4 py-2 text-sm text-gray-700'>
                    {index + 1} {index === 0 ? 'pallet' : 'pallets'}
                  </td>
                  <td className='px-4 py-2'>
                    <Controller
                      name={`palletRates.${index}.price`}
                      control={control}
                      render={({ field: inputField }) => (
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          placeholder='0.00'
                          value={inputField.value ?? ''}
                          onChange={(e) =>
                            inputField.onChange(
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          className='w-full max-w-[180px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        />
                      )}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Weight Rates Table */}
      {rateMode === 'weight' && (
        <div className='border border-gray-200 rounded-lg overflow-hidden'>
          <table className='w-full'>
            <thead>
              <tr className='bg-gray-50'>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Min Weight (kg)
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Max Weight (kg)
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Base Charge
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {weightFields.map((field, index) => (
                <tr key={field.id} className='hover:bg-gray-50'>
                  <td className='px-4 py-2'>
                    <Controller
                      name={`weightRates.${index}.minWeight`}
                      control={control}
                      render={({ field: inputField }) => (
                        <input
                          type='number'
                          step='1'
                          min='0'
                          placeholder='0'
                          value={inputField.value ?? ''}
                          onChange={(e) =>
                            inputField.onChange(
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          className='w-full max-w-[140px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        />
                      )}
                    />
                  </td>
                  <td className='px-4 py-2'>
                    <Controller
                      name={`weightRates.${index}.maxWeight`}
                      control={control}
                      render={({ field: inputField }) => (
                        <input
                          type='number'
                          step='1'
                          min='0'
                          placeholder={index === weightFields.length - 1 ? 'No limit' : '0'}
                          value={inputField.value ?? ''}
                          onChange={(e) =>
                            inputField.onChange(
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          className='w-full max-w-[140px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        />
                      )}
                    />
                  </td>
                  <td className='px-4 py-2'>
                    <Controller
                      name={`weightRates.${index}.price`}
                      control={control}
                      render={({ field: inputField }) => (
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          placeholder='0.00'
                          value={inputField.value ?? ''}
                          onChange={(e) =>
                            inputField.onChange(
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          className='w-full max-w-[180px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        />
                      )}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rate validation error */}
      {rateError && (
        <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
          <span className='text-sm text-red-700'>{rateError}</span>
        </div>
      )}

      {/* Additional Parameters */}
      <div className='flex flex-col gap-2'>
        <h4 className='text-base font-medium text-gray-900'>
          Additional Charges
        </h4>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <Controller
            name='maxOffRouteDistance'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label={`Max Distance Off-Route (${OFF_ROUTE_DISTANCE_UNIT})`}
                name='maxOffRouteDistance'
                type='number'
                placeholder='0'
                value={field.value?.toString() || ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />

          <Controller
            name='chargePerAdditionalDrop'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label='Charge per Extra Drop'
                name='chargePerAdditionalDrop'
                type='number'
                placeholder='0.00'
                value={field.value?.toString() || ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />

          <Controller
            name='standardIncludedLoadingMinutes'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label='STD Loading Time (min)'
                name='standardIncludedLoadingMinutes'
                type='number'
                placeholder='0'
                value={field.value?.toString() || ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />

          <Controller
            name='standardIncludedUnloadingMinutes'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label='STD Drop Off Time (min)'
                name='standardIncludedUnloadingMinutes'
                type='number'
                placeholder='0'
                value={field.value?.toString() || ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />

          <Controller
            name='chargePerAdditionalHour'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label='Cost per Hour'
                name='chargePerAdditionalHour'
                type='number'
                placeholder='0.00'
                value={field.value?.toString() || ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />

          <Controller
            name='chargePerOvertimeHour'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label='Overtime per Hour'
                name='chargePerOvertimeHour'
                type='number'
                placeholder='0.00'
                value={field.value?.toString() || ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                isInvalid={!!error}
                validationMessage={error?.message}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
